import { useState } from "react";
import { useQuery } from "react-query";
import { Box, Chip, CircularProgress, Paper, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from "recharts";

import AppAlert from "../../components/AppAlert";
import ChartLegend, {
  rechartsLegendEntries,
} from "../../components/ChartLegend";
import {
  getTooltipGraph,
  getTooltipGraphLabel,
  getTooltipGraphPayload,
} from "../../styles";
import { VARIABLE_LABELS } from "./constants";
import { BreakdownRow, fetchPredictionBreakdown } from "./services";

const BREAKDOWN_VARIABLES = [
  "type_client_nom_court",
  "canal_nandc_code",
  "code_tarifaire_nandc_code",
  "los_disc",
  "pays_nandc_code",
  "uh_type_nandc_code",
];

const BREAKDOWN_LABELS: Record<string, string> = Object.fromEntries(
  BREAKDOWN_VARIABLES.map((key) => [key, VARIABLE_LABELS[key] ?? key])
);

const BREAKDOWN_SERIES = [
  {
    key: "materialized_sales",
    label: "Ventes matérialisées",
    color: "var(--mui-palette-chart-positive)",
  },
  {
    key: "ml_cancellations",
    label: "Annulations prévues",
    color: "var(--mui-palette-chart-cancellation)",
  },
  {
    key: "cancelled_sales",
    label: "Ventes annulées (CXL)",
    color: "var(--mui-palette-chart-negative)",
  },
] as const;

const CURRENT_YEAR = new Date().getFullYear();

type BreakdownMode = "ventes" | "pct";

type YearFilter = "current" | "next" | "all";

const toPercent = (rows: BreakdownRow[]): BreakdownRow[] =>
  rows.map((r) => {
    const total = r.materialized_sales + r.ml_cancellations + r.cancelled_sales;
    if (total === 0) return r;
    return {
      ...r,
      materialized_sales:
        Math.round((r.materialized_sales / total) * 1000) / 10,
      ml_cancellations: Math.round((r.ml_cancellations / total) * 1000) / 10,
      cancelled_sales: Math.round((r.cancelled_sales / total) * 1000) / 10,
    };
  });

const BreakdownTooltip = ({
  active,
  payload,
  label,
  isPct,
}: {
  active?: boolean;
  payload?: { name: string; value: number; color: string }[];
  label?: string;
  isPct: boolean;
}) => {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <Box sx={{ ...getTooltipGraph, p: 1 }}>
      <Box sx={getTooltipGraphLabel}>
        <Typography variant="caption">{label}</Typography>
      </Box>
      {payload.map((entry) => (
        <Box
          key={entry.name}
          sx={{ ...getTooltipGraphPayload, alignItems: "center", gap: 0.75 }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: entry.color,
              flexShrink: 0,
            }}
          />
          <Typography variant="caption">
            {entry.name} :{" "}
            {isPct ? `${entry.value.toFixed(1)}%` : entry.value.toFixed(0)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const PredictionBreakdownChart = ({
  data,
  mode,
}: {
  data: BreakdownRow[];
  mode: BreakdownMode;
}) => {
  const theme = useTheme();
  if (!data || data.length === 0)
    return (
      <Typography
        variant="caption"
        sx={{
          color: "text.secondary",
        }}
      >
        Aucune donnée
      </Typography>
    );

  const isPct = mode === "pct";
  const chartData = isPct ? toPercent(data) : data;
  const axisColor = theme.vars.palette.chart.axis;

  return (
    <ResponsiveContainer width="100%" height={340}>
      <BarChart
        data={chartData}
        margin={{ top: 4, right: 16, left: 0, bottom: 20 }}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          stroke={theme.vars.palette.chart.grid}
        />
        <XAxis
          dataKey="modality"
          tick={{ fontSize: 11, fill: axisColor }}
          stroke={axisColor}
          interval={0}
        />
        <YAxis
          tick={{ fontSize: 11, fill: axisColor }}
          stroke={axisColor}
          tickFormatter={isPct ? (v) => `${v}%` : undefined}
          domain={isPct ? [0, 100] : undefined}
        />
        <ChartTooltip
          cursor={{ fill: "transparent" }}
          content={(props) => (
            <BreakdownTooltip
              active={props.active}
              payload={
                props.payload as {
                  name: string;
                  value: number;
                  color: string;
                }[]
              }
              label={props.label as string}
              isPct={isPct}
            />
          )}
        />
        <Legend
          content={({ payload }) => (
            <ChartLegend
              sx={{ pt: 1 }}
              entries={rechartsLegendEntries(payload)}
            />
          )}
        />
        {BREAKDOWN_SERIES.map((s) => (
          <Bar
            key={s.key}
            dataKey={s.key}
            name={s.label}
            fill={s.color}
            stackId="stack"
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

const PredictionBreakdown = ({
  activeFeatures,
}: {
  activeFeatures?: string[];
}) => {
  const [selectedVar, setSelectedVar] = useState<string>(
    BREAKDOWN_VARIABLES[0]!
  );
  const [mode, setMode] = useState<BreakdownMode>("ventes");
  const [yearFilter, setYearFilter] = useState<YearFilter>("all");

  const visibleLabels =
    activeFeatures && activeFeatures.length > 0
      ? Object.fromEntries(
          Object.entries(BREAKDOWN_LABELS).filter(([key]) =>
            activeFeatures.includes(key)
          )
        )
      : BREAKDOWN_LABELS;
  const effectiveLabels =
    Object.keys(visibleLabels).length > 0 ? visibleLabels : BREAKDOWN_LABELS;
  const effectiveVar =
    selectedVar in effectiveLabels
      ? selectedVar
      : Object.keys(effectiveLabels)[0]!;

  const year =
    yearFilter === "current"
      ? CURRENT_YEAR
      : yearFilter === "next"
      ? CURRENT_YEAR + 1
      : undefined;

  const {
    data: breakdown,
    isLoading,
    isError,
  } = useQuery(
    ["prediction-breakdown", year] as const,
    () => fetchPredictionBreakdown(year),
    {
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    }
  );

  return (
    <Paper elevation={1} sx={{ p: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.5,
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
          }}
        >
          Analyse prédictive par variable — données futures
        </Typography>
        <Box sx={{ display: "flex", gap: 0.75 }}>
          {(["ventes", "pct"] as BreakdownMode[]).map((m) => (
            <Chip
              key={m}
              label={m === "ventes" ? "Ventes" : "%"}
              size="small"
              onClick={() => setMode(m)}
              sx={{
                fontSize: "0.75rem",
                bgcolor: mode === m ? "primary.main" : "transparent",
                color: mode === m ? "primary.contrastText" : "text.secondary",
                border: "1px solid",
                borderColor: mode === m ? "primary.main" : "divider",
                "&:hover": {
                  bgcolor: mode === m ? "primary.dark" : "action.hover",
                },
              }}
            />
          ))}
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
          }}
        >
          Basé sur les réservations futures actives (BKD/INV) et annulées (CXL),
          avec le modèle ML actif.
        </Typography>
        <Box sx={{ display: "flex", gap: 0.75 }}>
          {(
            [
              ["all", "Toutes"],
              ["current", `${CURRENT_YEAR}`],
              ["next", `${CURRENT_YEAR + 1}`],
            ] as [YearFilter, string][]
          ).map(([key, label]) => (
            <Chip
              key={key}
              label={label}
              size="small"
              onClick={() => setYearFilter(key)}
              sx={{
                fontSize: "0.75rem",
                bgcolor: yearFilter === key ? "primary.main" : "transparent",
                color:
                  yearFilter === key
                    ? "primary.contrastText"
                    : "text.secondary",
                border: "1px solid",
                borderColor: yearFilter === key ? "primary.main" : "divider",
                "&:hover": {
                  bgcolor: yearFilter === key ? "primary.dark" : "action.hover",
                },
              }}
            />
          ))}
        </Box>
      </Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 2 }}>
        {Object.entries(effectiveLabels).map(([key, label]) => (
          <Chip
            key={key}
            label={label}
            size="small"
            onClick={() => setSelectedVar(key)}
            sx={{
              fontSize: "0.75rem",
              bgcolor: effectiveVar === key ? "primary.main" : "transparent",
              color:
                effectiveVar === key
                  ? "primary.contrastText"
                  : "text.secondary",
              border: "1px solid",
              borderColor: effectiveVar === key ? "primary.main" : "divider",
              "&:hover": {
                bgcolor: effectiveVar === key ? "primary.dark" : "action.hover",
              },
            }}
          />
        ))}
      </Box>
      {isLoading && <CircularProgress size={24} />}
      {isError && (
        <AppAlert severity="error">
          Erreur lors du chargement du breakdown
        </AppAlert>
      )}
      {breakdown && (
        <PredictionBreakdownChart
          data={breakdown[effectiveVar] ?? []}
          mode={mode}
        />
      )}
    </Paper>
  );
};

export default PredictionBreakdown;
